import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import classNames from "classnames";
import { useEffect } from "react";
import TableInputGroup from "../../../../ui/TableInputGroup/TableInputGroup";
import TableInput from "../../../../ui/TableInputGroup/TableInput";
import { PatternSymbolColor } from "../../../../__generated__/gql/graphql";
import PatternPreview from "../../components/PatternPreview";
import type { CreatePatternData } from "../CreatePattern/CreatePattern";
import styles from "./ConfigureSymbols.module.scss";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Feedback from "../../../../ui/Feedback/Feedback";

const schema = z.object({
  symbols: z
    .object({
      color: z.nativeEnum(PatternSymbolColor),
      name: z.string(),
      values: z
        .array(
          z.object({
            value: z.string().nonempty("Required"),
            type: z.literal("STRING"),
          }),
        )
        .min(1)
        .refine(
          (values) =>
            new Set(values.map((value) => value.value)).size === values.length,
          "Symbols must be unique",
        ),
    })
    .array(),
});

export type ConfigureSymbolsFormData = z.infer<typeof schema>;

function ConfigureSymbol({ index }: { index: number }) {
  const {
    control,
    watch,
    setValue,
    setFocus,
    formState: { errors },
  } = useFormContext<ConfigureSymbolsFormData>();
  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: `symbols.${index}.values`,
  });

  const symbol = watch(`symbols.${index}`);

  return (
    <div className={styles.table}>
      <TableInputGroup
        className={{
          header: classNames({
            [styles.tableHeaderPurple]:
              symbol.color === PatternSymbolColor.Purple,
            [styles.tableHeaderGreen]:
              symbol.color === PatternSymbolColor.Green,
            [styles.tableHeaderBlue]: symbol.color === PatternSymbolColor.Blue,
            [styles.tableHeaderCyan]: symbol.color === PatternSymbolColor.Cyan,
          }),
        }}
        header={
          <>
            <span className={styles.tableHeaderTitle}>{symbol.name}</span>
            <span className={styles.keywordsCount}>{symbol.values.length}</span>
          </>
        }
      >
        {fields.map((field, i) => (
          <Controller
            control={control}
            key={field.id}
            name={`symbols.${index}.values.${i}.value`}
            render={({
              field: { value, onChange, ref },
              fieldState: { error },
            }) => (
              <TableInput
                error={error?.message}
                addValue={(o) => {
                  if (o?.index) {
                    insert(
                      o.index,
                      { value: o?.value ?? "", type: "STRING" },
                      { shouldFocus: true },
                    );
                  } else {
                    append(
                      { value: o?.value ?? "", type: "STRING" },
                      { shouldFocus: true },
                    );
                  }
                }}
                addValues={(newValues) => {
                  const valuesToAppend = newValues;

                  if (value === "") {
                    setValue(`symbols.${index}.values.${i}`, {
                      value: valuesToAppend.shift() ?? "",
                      type: "STRING",
                    });
                  }

                  if (valuesToAppend.length > 0) {
                    append(
                      valuesToAppend.map((newValue) => ({
                        value: newValue ?? "",
                        type: "STRING",
                      })),
                      { shouldFocus: true },
                    );
                  }
                }}
                index={i}
                key={field.id}
                onChange={onChange}
                ref={ref}
                removeValue={() => {
                  remove(i);
                }}
                setFocus={(newIndex) => {
                  setFocus(`symbols.${index}.values.${newIndex}.value`);
                }}
                totalCount={fields.length}
                value={value}
              />
            )}
          />
        ))}
      </TableInputGroup>
      {errors.symbols?.[index]?.values?.root && (
        <Feedback>
          {errors.symbols?.[index]?.values?.root?.message ?? ""}
        </Feedback>
      )}
    </div>
  );
}
function ConfigureSymbols({
  templateNodes,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type,
  symbols,
  onChange,
  onSubmit,
  onEditPattern,
}: {
  templateNodes: CreatePatternData["templateNodes"];
  type: CreatePatternData["type"];
  symbols: CreatePatternData["symbols"];
  onChange: (data: ConfigureSymbolsFormData) => void;
  onSubmit: (data: ConfigureSymbolsFormData) => void;
  onEditPattern: () => void;
}) {
  const methods = useForm<ConfigureSymbolsFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      symbols: symbols.map((symbol) => ({
        ...symbol,
        values: symbol.values.length
          ? symbol.values.map((value) => ({ value, type: "STRING" }))
          : [{ value: "", type: "STRING" }],
      })),
    },
  });

  const { watch, handleSubmit, getValues } = methods;

  useEffect(() => {
    onChange(getValues());

    const subscription = watch((data) => {
      /**
       * This defaulting is just because of the types from react-hook-form. None of these values
       * should be undefined
       */
      onChange({
        symbols:
          data.symbols?.map((symbol) => ({
            name: symbol?.name ?? "",
            color: symbol?.color ?? PatternSymbolColor.Purple,
            values:
              symbol?.values?.map((value) => ({
                value: value?.value ?? "",
                type: "STRING",
              })) ?? [],
          })) ?? [],
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [getValues, onChange, watch]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <PatternPreview
          onEditPattern={onEditPattern}
          symbols={symbols}
          templateNodes={templateNodes}
          // type={type}
          showSubmit
        />
        <div className={styles.form}>
          {symbols.map((a, index) => (
            <ConfigureSymbol index={index} key={a.name} />
          ))}
        </div>
      </form>
    </FormProvider>
  );
}

export default ConfigureSymbols;
